import { useEffect } from 'react';

import { Avatar, Button, Callout, Flex, Text } from '@raystack/apsara';

import { useCopilotDispatch, useCopilotSelector } from '@src/contexts';
import { Show } from '@src/hoc';
import { useCopilot } from '@src/hooks/useCopilot';
import {
  AURORA_INTELLIGENCE_THUMBNAIL_16,
  MULTI_CHAT_STEPS
} from '@src/utils/constants/COPILOT_CONSTANTS';
import { PromptsProps } from '@src/utils/types/CopilotTypes';

import { PromptsSkeleton } from './Loaders';
import { Prompts } from './Prompts';

import styles from './MultiChat.module.css';

const MsgFetchFailedScreen = () => {
  const chatId = useCopilotSelector(state => state.chatId);
  const retryCount = useCopilotSelector(state => state.retryCount);
  const { fetchMessages } = useCopilot();
  const dispatch = useCopilotDispatch();

  const retryMsgFetch = async () => {
    dispatch({
      multiChatStep: MULTI_CHAT_STEPS.CHAT,
      retryCount: retryCount + 1
    });
    await fetchMessages(chatId);
  };

  return (
    <Flex
      gap={3}
      align='start'
      justify='start'
      className={styles.msgFetchFailedWrapper}
    >
      <Avatar size={2} radius='small' src={AURORA_INTELLIGENCE_THUMBNAIL_16} />
      <Callout
        className={styles.retryCallout}
        type='alert'
        action={
          <Show when={retryCount < 3}>
            <Button
              variant='outline'
              color='neutral'
              size='small'
              data-test-id='retry-msg-btn-click'
              onClick={retryMsgFetch}
            >
              Retry
            </Button>
          </Show>
        }
      >
        <Show
          when={retryCount < 3}
          fallback={
            <>
              We couldn't restore your chat. Start a new one or try again later.
              <br /> <br />
              Note: Aurora won't have previous context.
            </>
          }
        >
          We're having trouble loading your chat history. You can try again
          reloading the chat or start a new conversation.
        </Show>
      </Callout>
    </Flex>
  );
};

export const WelcomeScreen = ({ handleSelectPrompt }: PromptsProps) => {
  const { fetchPrompts } = useCopilot();
  const isFetchingPrompts = useCopilotSelector(
    state => state.isFetchingPrompts
  );

  const msgFetchFailed = useCopilotSelector(state => state.msgFetchFailed);

  const dispatch = useCopilotDispatch();

  useEffect(() => {
    fetchPrompts();
    dispatch({ createChatFailed: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Show when={!msgFetchFailed} fallback={<MsgFetchFailedScreen />}>
      <div className={styles.welcomeWrapper}>
        <Flex direction='column' align='start' gap={8}>
          <Flex direction='column' align='start' gap={6}>
            <Avatar
              size={6}
              radius='small'
              src={AURORA_INTELLIGENCE_THUMBNAIL_16}
            />
            <Text weight='medium' size='regular'>
              Good to see you! <br /> How can I be of assistance?
            </Text>
          </Flex>
          <Show when={!isFetchingPrompts} fallback={<PromptsSkeleton />}>
            <Prompts handleSelectPrompt={handleSelectPrompt} />
          </Show>
        </Flex>
      </div>
    </Show>
  );
};
